import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSessionContext } from 'contexts/session';
import { theme, toRem } from 'design';

const Layout = styled.div`
  position: fixed;
  display: relative;
  justify-content: center;
  background: ${theme.color.success.medium};
  padding: ${toRem(4)} 0;
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.medium};
  width: 100%;
  height: ${toRem(24)};
  top: 0;

  @media (min-width: ${theme.breakpoint.med}px) {
    position: absolute;
    left: 50%;
    width: auto;
    min-width: ${toRem(200)};
    transform: translate(-50%, 0);
    clip-path: polygon(8% 100%, 0% 0%, 100% 0%, 92% 100%);
    z-index: ${theme.zIndex.header + 1};
  }
`;

interface IRemoteSessionHeaderProps {}

// Helper function to format time in mm:ss format.
const formatTime = (seconds: number) => {
  if (seconds <= 0) return '00:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

// Helper function to get the countdown time remaining.
// When the time has expired, the session will be set to expires in the session context and log out the user.
const useCountdown = (initialTime: number) => {
  const { session, setSession } = useSessionContext();
  const [timeRemaining, setTimeRemaining] = useState(initialTime);
  const endTimeRef = useRef(Date.now() + initialTime * 1000);

  useEffect(() => {
    const updateRemainingTime = () => {
      const now = Date.now();
      const timeLeft = Math.max(0, Math.floor((endTimeRef.current - now) / 1000));
      setTimeRemaining(timeLeft);
      if (timeLeft === 0) {
        clearInterval(intervalId);
        setSession({ ...session, isWarningPeriod: false, isExpired: true });
      }
    };

    const intervalId = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(intervalId); // Cleanup the interval on component unmount
  }, []);

  return timeRemaining;
};

const RemoteSessionHeader: React.FC<IRemoteSessionHeaderProps> = () => {
  const { session } = useSessionContext();
  const timeRemaining = session && useCountdown(Math.trunc(session?.secondsRemaining));

  return <Layout data-cy="remote-session-tag">Remote Session - {formatTime(timeRemaining)}</Layout>;
};

export default RemoteSessionHeader;
